import { useEffect, useMemo, useState } from "react";
import backendApiService from "../service/backend-api.service";
import { keyOf } from "./favoriteKey";

const TTL_MS = 5 * 60 * 1000;
const cache = new Map();

const cacheKey = (item) => `${keyOf(item)}@${item.region || ""}`;

function fresh(item, now) {
  const hit = cache.get(cacheKey(item));
  return hit && now - hit.at < TTL_MS;
}

export function clearFavoritePricesCache() {
  cache.clear();
}

/**
 * Текущие цены избранного: { prices: { "<platform>|<gameKey>": цена | null }, loading, failed }.
 * Цена запрашивается для региона записи; null — цены нет, показывается сохранённая.
 */
export function useFavoritePrices(items) {
  const signature = items.map(cacheKey).sort().join(",");
  const [state, setState] = useState({ version: 0, loading: false, failed: false });

  useEffect(() => {
    const now = Date.now();
    const need = items.filter((item) => item.platform !== "playstation" && !fresh(item, now));
    if (!need.length) return undefined;
    let alive = true;
    setState((current) => ({ ...current, loading: true, failed: false }));
    backendApiService
      .getFavoritePrices(
        need.map(({ platform, gameKey, region, nsuid }) => ({ platform, gameKey, region, nsuid })),
      )
      .then((prices) => {
        const at = Date.now();
        need.forEach((item) => {
          cache.set(cacheKey(item), { at, value: prices[keyOf(item)] ?? null });
        });
        if (alive) setState((current) => ({ version: current.version + 1, loading: false, failed: false }));
      })
      .catch(() => {
        if (alive) setState((current) => ({ ...current, loading: false, failed: true }));
      });
    return () => {
      alive = false;
    };
    // items меняются при каждом рендере; запрос зависит только от ключей и регионов
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const prices = useMemo(
    () =>
      Object.fromEntries(
        items.map((item) => [keyOf(item), cache.get(cacheKey(item))?.value ?? null]),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature, state.version],
  );

  return { prices, loading: state.loading, failed: state.failed };
}
